import { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  CategoryScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { startOfISOWeek, addDays, getISOWeek } from "date-fns";
import "chartjs-adapter-date-fns";
import { sv } from "date-fns/locale";
import "./Chart.scss";
import { buildDateMap } from "./ChartUtils";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  CategoryScale
);

interface CoachChartAllMemberTypesProps {
  rows: any[];
  startDate: string;
  endDate: string;
}

const orderedDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export function CoachChartAllMemberTypes({
  rows = [],
  startDate,
  endDate,
}: CoachChartAllMemberTypesProps) {
  const [groupByWeek, setGroupByWeek] = useState(true);
  const [chartMax, setChartMax] = useState<number | null>(null);

  const weeklyRows = rows.filter((r) => r.type === "row");

  // ─────────────────────────────────────
  // Date helpers
  // ─────────────────────────────────────
  const dateMap = buildDateMap(startDate, endDate);

  // ─────────────────────────────────────
  // Build buckets:
  // time → memberType → { total, coaches }
  // ─────────────────────────────────────
  const buckets: Record<
    string,
    {
      label: string | Date;
      memberTypes: Record<
        string,
        { total: number; coaches: Record<string, number> }
      >;
    }
  > = {};

  weeklyRows.forEach((row) => {
    orderedDays.forEach((day) => {
      const cell = row[day];
      if (!cell) return;

      const week = row.timespan.split("v.")[1]?.trim();
      const date = dateMap.get(`${week}-${day}`);
      if (!date) return;

      const key = groupByWeek ? row.timespan : date.toISOString();

      if (!buckets[key]) {
        buckets[key] = {
          label: groupByWeek ? `Vecka ${row.timespan}` : date,
          memberTypes: {},
        };
      }

      cell.memberTypes?.forEach((mt: any) => {
        if (!buckets[key].memberTypes[mt.memberType]) {
          buckets[key].memberTypes[mt.memberType] = {
            total: 0,
            coaches: {},
          };
        }

        buckets[key].memberTypes[mt.memberType].total += mt.count;

        const coachName = row.coachName ?? "Okänd";
        buckets[key].memberTypes[mt.memberType].coaches[coachName] =
          (buckets[key].memberTypes[mt.memberType].coaches[coachName] ?? 0) +
          mt.count;
      });
    });
  });

  // ─────────────────────────────────────
  // Extract memberTypes
  // ─────────────────────────────────────
  const memberTypes = Array.from(
    new Set(Object.values(buckets).flatMap((b) => Object.keys(b.memberTypes)))
  );

  const labels = Object.values(buckets).map((b) => b.label);

  const totalData = Object.values(buckets).map((b) =>
    Object.values(b.memberTypes).reduce((sum, mt) => sum + mt.total, 0)
  );

  // ─────────────────────────────────────
  // Datasets (ONE PER MEMBER TYPE)
  // ─────────────────────────────────────
  const colors = [
    "#3F9C30",
    "#309C49",
    "#309C6B",
    "#309C8C",
    "#30849C",
    "#30629C",
    "#303F9C",
    "#49309C",
  ];

  const datasets = [
    {
      label: "Totalt",
      data: totalData,
      borderColor: "#30389c",
      backgroundColor: "#30389c55",
      borderDash: [6, 4],
      borderWidth: 2,
      pointRadius: 3,
      tension: 0.1,
    },
    ...memberTypes.map((mt, idx) => ({
      label: mt,
      data: Object.values(buckets).map((b) => b.memberTypes[mt]?.total ?? 0),
      coachBreakdown: Object.values(buckets).map(
        (b) => b.memberTypes[mt]?.coaches ?? {}
      ),
      borderColor: colors[idx % colors.length],
      backgroundColor: colors[idx % colors.length] + "55",
      borderWidth: 2,
      tension: 0.1,
    })),
  ];

  const numericValues = datasets
    .flatMap((d) => d.data)
    .filter((v): v is number => v != null && !isNaN(v));

  const maxValue = Math.max(...numericValues, 10);

  useEffect(() => {
    if (maxValue) setChartMax(Math.ceil(maxValue * 1.1));
  }, [maxValue, groupByWeek]);

  const rangeOptions = [0.25, 0.5, 0.75, 1.1, 1.5].map((factor) => ({
    label: `${Math.round(maxValue * factor)}`,
    value: Math.round(maxValue * factor),
  }));

  if (!rows.length) return <div>No data</div>;
  return (
    <div className="small-coach-chart text-m">
      <div className="chart-controls">
        <span style={{ marginRight: "auto" }}>
          Tränarbokningar per spelrätt
        </span>
        <div className="chart-max-select">
          <label>Maxvärde:</label>
          <select
            className="chart-max-select-dropdown"
            value={chartMax ?? ""}
            onChange={(e) =>
              setChartMax(e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value={Math.ceil(maxValue * 1.1)}>Auto</option>
            {rangeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="chart-view-toggle">
          Visa per vecka
          <label className="toggle text-m">
            <input
              type="checkbox"
              checked={groupByWeek}
              onChange={(e) => setGroupByWeek(e.target.checked)}
            />
            <span className="slider round" />
          </label>
        </div>
      </div>

      <Line
        data={{ labels: labels as any, datasets }}
        options={{
          scales: {
            x: groupByWeek
              ? {
                  type: "category",
                  title: { display: true, text: "Vecka" },
                }
              : {
                  type: "time",
                  time: {
                    unit: "day",
                    tooltipFormat: "EEE dd/MM",
                    displayFormats: { day: "EEE dd/MM" },
                  },
                  adapters: { date: { locale: sv } },
                  title: { display: true, text: "Datum" },
                },
            y: {
              beginAtZero: true,
              min: 0,
              max: Number(chartMax),
              ticks: { stepSize: 1, precision: 0 },
              title: { display: true, text: "Bokningar" },
            },
          },
          plugins: {
            legend: { position: "top" },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const breakdown =
                    ctx.dataset.coachBreakdown?.[ctx.dataIndex] ?? {};
                  const lines = [`${ctx.dataset.label}: ${ctx.parsed.y}`];

                  Object.entries(breakdown)
                    .sort((a: any, b: any) => b[1] - a[1])
                    .forEach(([coach, count]) => {
                      lines.push(`${coach}: ${count}`);
                    });

                  return lines;
                },
              },
            },
          },
        }}
      />
    </div>
  );
}
